import { Editor } from 'components/Editor/Editor'
import { EditorRHF } from 'components/Editor/EditorRHF'
import { INote } from 'models/contact_companies'
import { useState, useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { CgAttachment } from 'react-icons/cg'
import { Paperclip, X } from 'lucide-react'
import { toast } from 'react-toastify'
import { API_HOST } from 'utils/http-client'
import { useFileAttachments } from 'hooks/useFileAttachments'
import {
  ALLOWED_NOTE_ATTACHMENT_TYPES,
  MAX_NOTE_ATTACHMENT_SIZE,
  getAcceptString,
} from 'constants/fileTypes'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'

const MAX_FILENAME_LENGTH = 20

/** Truncate a filename before its extension, preserving the extension. */
function truncateFilename(name: string, maxLen = MAX_FILENAME_LENGTH): string {
  if (name.length <= maxLen) return name
  const dotIndex = name.lastIndexOf('.')
  if (dotIndex <= 0) return name.slice(0, maxLen - 3) + '...'
  const ext = name.slice(dotIndex)
  const base = name.slice(0, dotIndex)
  const available = maxLen - ext.length - 3 // 3 for "..."
  if (available <= 0) return name.slice(0, maxLen - 3) + '...'
  return base.slice(0, available) + '...' + ext
}

interface NoteManagerProps<T extends FieldValues> {
  control: Control<T>
  noteName: Path<T>
  attachmentsName: Path<T>
  label?: string
  placeholder?: string
  existingNote?: INote
  viewMode?: boolean
  editorClassName?: string
  enableMentions?: boolean
  disabled?: boolean
  onAttachmentsToDelete?: (attachmentIds: number[]) => void
  /** Content rendered after the toolbar, pushed to the right (e.g. a Save button). */
  toolbarSuffix?: React.ReactNode
  /** Additional className applied to the toolbar wrapper (e.g. background color). */
  toolbarClassName?: string
}

export const NoteManager = <T extends FieldValues>(
  props: NoteManagerProps<T>
) => {
  const {
    control,
    noteName,
    attachmentsName,
    label,
    placeholder,
    existingNote,
    viewMode,
    editorClassName = 'h-28',
    enableMentions = false,
    disabled = false,
  } = {
    placeholder: 'Start writing note here',
    label: 'New Note',
    ...props,
  }

  const [existingAttachments, setExistingAttachments] = useState(
    existingNote?.attachments
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([])

  const {
    field: { onChange: attachmentsOnChange, value: attachmentsValue },
  } = useController({ name: attachmentsName, control }) as {
    field: { onChange: (value: File[]) => void; value: File[] }
  }

  const {
    fileInputRef: inputRef,
    openFileSelect,
    handleFileSelect,
  } = useFileAttachments({
    allowedTypes: ALLOWED_NOTE_ATTACHMENT_TYPES,
    maxFileSize: MAX_NOTE_ATTACHMENT_SIZE,
    maxTotalSize: MAX_NOTE_ATTACHMENT_SIZE,
    onAdd: (newAttachments) => {
      const files = newAttachments
        .filter(isNewAttachment)
        .map((att) => att.file)
      attachmentsOnChange([...attachmentsValue, ...files])
    },
  })

  const handleRemoveExistingAttachment = (index: number) => {
    if (!existingAttachments) return
    const attachment = existingAttachments[index]

    // Track for deletion (API call happens on save)
    if (attachment.id) {
      setAttachmentsToDelete((prev) => {
        const updated = [...prev, attachment.id]
        props.onAttachmentsToDelete?.(updated)
        return updated
      })
    }

    // Remove from display immediately
    setExistingAttachments((existingAttachments) =>
      existingAttachments?.filter((_, i) => i !== index)
    )
    toast.info('Attachment will be removed when you save')
  }

  // Combine existing and new attachments for AttachmentList
  const allAttachments = useMemo(() => {
    const combined: Attachment[] = []

    // Add existing attachments from server
    if (existingAttachments) {
      combined.push(
        ...existingAttachments.map((att) => ({
          id: att.id,
          filename: att.original_filename,
          size: att.size || 0, // Use size from backend
          file_url: att.file?.startsWith('/')
            ? `${API_HOST}${att.file}`
            : att.file,
        }))
      )
    }

    // Add new attachments (Files)
    if (!viewMode && attachmentsValue) {
      combined.push(
        ...attachmentsValue.map((file) => ({
          file,
          filename: file.name,
          size: file.size,
        }))
      )
    }

    return combined
  }, [existingAttachments, attachmentsValue, viewMode])

  const shouldShowSummary = allAttachments.length > 0

  const handleRemoveAttachment = (index: number) => {
    const existingCount = existingAttachments?.length || 0

    if (index < existingCount) {
      // Remove existing attachment
      handleRemoveExistingAttachment(index)
    } else {
      // Remove new attachment
      const newIndex = index - existingCount
      const newAttachments = attachmentsValue.filter((_, i) => i !== newIndex)
      attachmentsOnChange(newAttachments)
      toast.info('Attachment removed')
    }
  }

  /** Compose toolbar suffix: inline attachment chips + parent's toolbarSuffix. */
  const composedToolbarSuffix = useMemo(() => {
    const chips =
      !viewMode && allAttachments.length > 0 ? (
        <>
          {allAttachments.map((att, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2 h-6 rounded bg-surface-hover text-xxs text-fg-secondary max-w-[180px]"
              title={att.filename}
            >
              <Paperclip size={10} className="flex-shrink-0" />
              <span className="truncate">{truncateFilename(att.filename)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(i)}
                  className="flex-shrink-0 hover:text-error transition-colors"
                >
                  <X size={10} />
                </button>
              )}
            </span>
          ))}
        </>
      ) : null

    if (!chips && !props.toolbarSuffix) return undefined
    return (
      <>
        {chips}
        {props.toolbarSuffix}
      </>
    )
  }, [allAttachments, viewMode, disabled, props.toolbarSuffix]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {viewMode ? (
        <Editor
          value={existingNote?.content || ''}
          readOnly
          disableToolbar
          borderless
        />
      ) : (
        <EditorRHF
          name={noteName}
          className={editorClassName}
          control={control}
          label={label}
          placeholder={placeholder}
          enableMentions={enableMentions}
          disabled={disabled}
          additionalToolbarButtons={[
            <CgAttachment
              key="attachments"
              size={24}
              className={`p-1 w-8 ${
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:text-brand-active'
              }`}
              onClick={disabled ? undefined : openFileSelect}
            />,
          ]}
          toolbarSuffix={composedToolbarSuffix}
          toolbarClassName={props.toolbarClassName}
        />
      )}

      {/* View-mode attachment list (full display, not inline) */}
      {viewMode && allAttachments.length > 0 && (
        <div className="mt-3">
          <AttachmentList
            attachments={allAttachments}
            disabled={disabled}
            maxSize={MAX_NOTE_ATTACHMENT_SIZE}
            showSummary={shouldShowSummary}
          />
        </div>
      )}

      {!viewMode && (
        <input
          type="file"
          hidden
          multiple
          ref={inputRef}
          onChange={(e) => {
            // Convert existing File[] to Attachment[] for validation
            const currentAttachments = attachmentsValue.map((file) => ({
              file,
              filename: file.name,
              size: file.size,
            }))
            handleFileSelect(e, currentAttachments)
          }}
          accept={getAcceptString(ALLOWED_NOTE_ATTACHMENT_TYPES)}
          disabled={disabled}
        />
      )}
    </div>
  )
}
